import { useEffect, useRef } from 'react';
import useLiveSessionStore from '@/channels/liveSession/liveSessionStore';
import useStudentLiveSessionStore from '@/channels/studentLiveSession/studentLiveSessionStore';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import { logLiveSessionEvent, navigationType, setPageLeftProvider } from '@/modules/liveSessionEvents';
import useSessionNavigationLock from './useSessionNavigationLock';

interface ForensicSlide {
  id: number | string;
  position?: number;
  type?: string;
}

interface UseLiveSessionForensicsArgs {
  // The store's activeSessionId — set by hydration or a later join_notification.
  activeSessionId: string | null;
  // The session the page load was evaluated against, even when hydration denied the student.
  forensicsSessionId?: string | null;
  // The server-side hydration result at page load (props.activeSessionId).
  hydratedSessionId?: string | null;
  selectedSlide: ForensicSlide | null;
  slideShowId: number | string;
  startingSlide?: number;
  userType: string;
}

type ReleaseReason =
  | 'teacher_disconnected' | 'session_ended' | 'student_removed' | 'channel_rejected' | 'session_cleared';

const entryModeFor = (via: string | null, navType: string | null): string => {
  if (via === 'popup' || via === 'teacher_popup') return 'popup';
  if (navType === 'reload') return 'reload';
  if (navType === 'back_forward') return 'back_forward';
  return 'direct';
};

// AIDEV-NOTE: Derived-state emitter for the forensic log. The lock, the socket state, and the
// applied slide are all client-only; this hook is the single place they get reported. Reason-
// carrying events (suppressed joins, debounce expiry) are logged at the line that knows the reason.
const useLiveSessionForensics = ({
  activeSessionId,
  forensicsSessionId = null,
  hydratedSessionId = null,
  selectedSlide,
  slideShowId,
  startingSlide,
  userType,
}: UseLiveSessionForensicsArgs) => {
  const isStudent = userType === 'Student';
  const sessionId = activeSessionId ?? hydratedSessionId ?? forensicsSessionId;

  const { isNavigationLocked } = useSessionNavigationLock();
  const disconnectedSessionId = useSlideShowContext(state => state.disconnectedSessionId);
  const connectionState = useLiveSessionStore(state => state.connectionState);
  const currentSlide = useLiveSessionStore(state => state.currentSlide);
  const sessionEndedReason = useLiveSessionStore(state => state.sessionEndedReason);
  const removalNotification = useStudentLiveSessionStore(state => state.removalNotification);

  const slideId = selectedSlide ? Number(selectedSlide.id) : null;
  const slideType = selectedSlide?.type === 'Transition' ? 'Transition' : 'Slide';

  // Latest values for effects that must not re-fire when these change.
  const snapshot = {
    connectionState,
    currentSlide,
    disconnectedSessionId,
    isNavigationLocked,
    position: selectedSlide?.position ?? null,
    removalNotification,
    sessionEndedReason,
    slideId,
  };
  const latestRef = useRef(snapshot);
  latestRef.current = snapshot;

  const lastSessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (sessionId) lastSessionIdRef.current = sessionId;
  }, [sessionId]);

  const enteredForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionId || enteredForRef.current === sessionId) return;
    enteredForRef.current = sessionId;
    const via = new URLSearchParams(window.location.search).get('via');
    const navType = navigationType();
    logLiveSessionEvent(sessionId, 'page_entered', {
      entry_mode: entryModeFor(via, navType),
      hydrated_session_id: hydratedSessionId,
      nav_type: navType,
      online: navigator.onLine,
      referrer: document.referrer || null,
      slide_show_id: Number(slideShowId),
      starting_slide: startingSlide ?? null,
      url: `${window.location.pathname}${window.location.search}`,
      user_type: userType,
      via,
      visibility_state: document.visibilityState,
    });
  }, [sessionId, hydratedSessionId, slideShowId, startingSlide, userType]);

  const prevLockedRef = useRef<boolean | null>(null);
  const lastReleaseReasonRef = useRef<ReleaseReason | null>(null);
  useEffect(() => {
    if (!isStudent) return;
    const prev = prevLockedRef.current;
    prevLockedRef.current = isNavigationLocked;
    if (prev === isNavigationLocked) return;
    if (prev === null && !isNavigationLocked) return;

    const targetSessionId = sessionId ?? lastSessionIdRef.current;
    if (isNavigationLocked) {
      let reason = 'join_notification';
      if (prev === null) reason = 'hydrated';
      else if (lastReleaseReasonRef.current === 'teacher_disconnected') reason = 'teacher_reconnected';
      logLiveSessionEvent(targetSessionId, 'lock_engaged', { active_session_id: activeSessionId, reason });
      return;
    }

    const latest = latestRef.current;
    let reason: ReleaseReason = 'session_cleared';
    if (latest.disconnectedSessionId && latest.disconnectedSessionId === lastSessionIdRef.current) {
      reason = 'teacher_disconnected';
    } else if (latest.sessionEndedReason) {
      reason = 'session_ended';
    } else if (latest.removalNotification) {
      reason = 'student_removed';
    } else if (latest.connectionState === 'rejected') {
      reason = 'channel_rejected';
    }
    lastReleaseReasonRef.current = reason;
    logLiveSessionEvent(targetSessionId, 'lock_released', { active_session_id: activeSessionId, reason });
  }, [isStudent, isNavigationLocked, sessionId, activeSessionId]);

  useEffect(() => {
    if (!isStudent || !sessionId || slideId === null) return;
    const { currentSlide: synced, isNavigationLocked: locked, position } = latestRef.current;
    const matchesTeacher = synced !== null
      && Number(synced.slideId) === slideId
      && synced.slideType === slideType;
    logLiveSessionEvent(sessionId, 'nav_applied', {
      cause: matchesTeacher ? 'sync' : 'user',
      locked,
      position,
      slide_id: slideId,
      slide_type: slideType,
    });
  }, [isStudent, sessionId, slideId, slideType]);

  useEffect(() => {
    setPageLeftProvider(() => ({
      metadata: {
        last_applied_slide: latestRef.current.slideId,
        lock_state: latestRef.current.isNavigationLocked ? 'locked' : 'free',
        ws_state: latestRef.current.connectionState,
      },
      sessionId: lastSessionIdRef.current,
    }));
    return () => setPageLeftProvider(null);
  }, []);
};

export default useLiveSessionForensics;
